import { Injectable } from "@angular/core";
import { AngularFireDatabase } from "angularfire2/database";
import { AngularFirestore } from "angularfire2/firestore";
import * as firebase from "firebase/app";
import { Observable } from "rxjs/Observable";
import { ICategory } from "../shared/models/categories.model";
import { IClientModules } from "../shared/models/client.model";
import { IFormsUsers } from "../shared/models/form-users.model";
import { IProfileUsers } from "../shared/models/profile-users.model";
import { HistoryData } from "./history";
import { _TRACKING_ENTITIES, TrackingProvider } from "./tracking";
import { Utilities } from "./utilities";

@Injectable()
export class ClientData {
  private action: string = "update";

  constructor(
    private afDb: AngularFireDatabase,
    private afstore: AngularFirestore,
    private historyData: HistoryData,
    private trackingAi: TrackingProvider,
    private utilities: Utilities,
  ) { }

  public getClients(): any {
    return this.afDb.list("ClientList").valueChanges();
  }

  public getResourcesByClientId(client: string): Observable<any[]> {
    return this.afDb
      .list<any>(`Clients/${client}/Resources`)
      .valueChanges();
  }

  public getUsersByClient(clientId: string): Observable<any[]> {
    return this.afDb
      .list<any>(`Clients/${clientId}/Users`)
      .valueChanges();
  }

  public getUserById(client: string, userId: string): Observable<any> {
    return this.afDb
      .object<any>(`Clients/${client}/Users/${userId}`)
      .valueChanges()
      .take(1);
  }

  public getResourcesById(client: string, resourceId): Observable<any> {
    return this.afDb
      .object<any>(`Clients/${client}/Resources/${resourceId}`)
      .valueChanges();
  }

  public getRaffleConfig(client: string): Observable<any> {
    return this.afDb
      .object<any>(`Clients/${client}/RaffleConfig`)
      .valueChanges()
      .take(1);
  }

  public getUsersRaffle(clientId: string): Observable<any[]> {
    return this.afDb
      .list(`Clients/${clientId}/RafflePrivate`)
      .valueChanges();
    // TODO: Deshabilitado mienstras se pasa a firestore
    // return this.afstore.collection(clientId).doc('raffle_private').collection('users')
    //   .valueChanges();
  }

  public deleteUserRaffle(clientId: string, codeId: string): Promise<any> {
    return this.afstore.collection(clientId).doc("raffle_private").collection("users").doc(codeId)
      .delete();
  }

  public getRaffleRewards(client: string): Observable<any[]> {
    return this.afDb
      .list<any>(`Clients/${client}/RaffleReward`)
      .valueChanges();
  }

  public pushResource(
    client: string,
    object: any,
    id: string = "",
  ): any {
    if (id === "") {
      this.action = "create";
      id = this.afDb.createPushId();
      object.created_at = new Date().getTime();
    } else {
      object.update_at = new Date().getTime();
    }

    object.id = id;

    return new Promise((res, rej) => {
      this.afDb.database
        .ref(`Clients/${client}/Resources/${id}`)
        .update(object)
        .then(() => {
          return res(id);
        })
        .catch((e) => {
          return rej(e);
        });
    });
  }

  public getClientConfig(clientId): any {
    return firebase.database().ref(`/ClientList/${clientId}`);
  }

  public setClientConfigModule(client: string, object: any, module: string): any {
    return new Promise((res, rej) => {
      this.afDb.database
        .ref(`ClientList/${client}/modules_config/${module}`)
        .update({
          ...object,
          id: module,
        })
        .then(() => {
          return res(client);
        })
        .catch((e) => {
          return rej(e);
        });
    });
  }

  public getClientConfigModule(client: string, module: string): any {
    return this.afDb
      .object(`ClientList/${client}/modules_config/${module}`)
      .valueChanges();
  }

  /**
   * Get all the client categories
   * @param clientId
   */
  public getClientCategories(clientId: string): Observable<ICategory[]> {
    return this.afDb.list<ICategory>(`/ClientList/${clientId}/categories`).valueChanges();
  }

  /**
   * Get the client categories where the state is true
   * @param clientId
   */
  public getClientCategoriesActive(clientId: string): Observable<any> {
    return this.afDb.list<ICategory>(`/ClientList/${clientId}/categories`, (ref) => {
      return ref.orderByChild("state").equalTo(true);
    }).valueChanges();
  }

  public getClientConfigObservable(clientId): Observable<any> {
    return this.afDb.object(`/ClientList/${clientId}`).valueChanges();
  }

  public getClientModules(clientId): Observable<IClientModules> {
    return this.afDb.object<IClientModules>(`/ClientList/${clientId}/modules`).valueChanges();
  }

  public getClientSponsors(clientId): Observable<any[]> {
    return this.afDb.list(`/ClientList/${clientId}/Sponsor`).valueChanges();
  }

  public getClientProfiles(clientId): Observable<IProfileUsers[]> {
    return this.afDb.list<IProfileUsers>(`/Clients/${clientId}/Profiles`).valueChanges();
  }

  public getClientFormUsers(clientId): Observable<IFormsUsers[]> {
    return this.afDb.list<IFormsUsers>(`/Clients/${clientId}/FormUsers`).valueChanges();
  }

  public getClientFormUser(clientId, formId): Observable<IFormsUsers> {
    return this.afDb.object<IFormsUsers>(`/Clients/${clientId}/FormUsers/${formId}`).valueChanges();
  }

  public getClientActiveProfiles(clientId): Observable<IProfileUsers[]> {
    return this.afDb.list<IProfileUsers>(
      `/Clients/${clientId}/Profiles`,
      (ref) => ref.orderByChild("state").equalTo(true),
    ).valueChanges();
  }

  public getClientSponsor(clientId, sponsorId): Observable<any> {
    return this.afDb.object(`/ClientList/${clientId}/Sponsor/${sponsorId}`).valueChanges();
  }

  public getClientStaffs(clientId): Observable<any[]> {
    return this.afDb.list(`/ClientList/${clientId}/Staff`).valueChanges();
  }

  public getClientReferred(clientId): Observable<any> {
    return this.afDb.object(`/Clients/${clientId}/Referred`).valueChanges();
  }

  public getClientAuthConfig(clientId): Observable<any> {
    return this.afDb.object(`/ClientList/${clientId}/AuthConfig`).valueChanges();
  }

  public getClientBusinessFormList(clientId): Observable<any[]> {
    return this.afDb.list(`/Clients/${clientId}/Interactivity/Business/Form`).valueChanges();
  }

  public getClientBusinessAgendaFormList(clientId): Observable<any[]> {
    return this.afDb.list(`/Clients/${clientId}/Interactivity/Business/Agenda`).valueChanges();
  }

  public getClientStaff(clientId, staffId): Observable<any> {
    return this.afDb.object(`/ClientList/${clientId}/Staff/${staffId}`).valueChanges();
  }

  public getClientQuestions(clientId: string): Observable<any[]> {
    return this.afDb.list(`/Clients/${clientId}/Questions`).valueChanges();
  }

  public setQuestion(
    client: string,
    questionId: string,
    question: any,
  ) {
    question.created_at = new Date().getTime();
    return new Promise((res, rej) => {
      this.afDb.database
        .ref(`Clients/${client}/Questions/${questionId}/`)
        .update(question)
        .then(() => {
          return res();
        })
        .catch((e) => {
          return rej(e);
        });
    });
  }

  public setQuestionArchive(client: string, questionId: string) {
    return this.afDb.object(`/Clients/${client}/Questions/${questionId}/archive`).set(true);
  }

  public unSetQuestionArchive(client: string, questionId: string) {
    return this.afDb.object(`/Clients/${client}/Questions/${questionId}/archive`).set(false);
  }

  public deleteQuestion(client: string, questionId: string) {
    return this.afDb.object(`/Clients/${client}/Questions/${questionId}`).remove();
  }

  public getClientQuestionsPublic(clientId: string): Observable<any[]> {
    return this.afDb.list(`/Clients/${clientId}/Questions`, (ref) => {
      return ref.orderByChild("public").equalTo(true);
    }).valueChanges();
  }

  public async setClientConfig(object: any, clientId: string = "") {
    if (!clientId) {
      object.created_at = new Date().getTime();
      clientId = this.afDb.createPushId();
      this.action = "create";
    } else {
      object.update_at = new Date().getTime();
    }

    if (Object.keys(object.categories).length === 0) {
      // Si el cliente es nuevo se insertan las categorias default
      object.categories = await this.utilities.getDefaultCategories();
    }

    object.id = clientId;

    return new Promise((res, rej) => {
      this.utilities.consoleDecorator("Object client", object);
      return firebase
        .database()
        .ref(`/ClientList/${clientId}`)
        .update(object)
        .then(() => {
          this.historyData.setClient(
            "General description",
            "client",
            this.action,
            object,
          );

          // El track solo se habilita si ya cuenta con categorias creadas
          if (object.categories) {
            this.trackingAi.trackEntity(_TRACKING_ENTITIES.CLIENT, clientId, clientId, object);
          }
          res(clientId);
        })
        .catch((e) => {
          rej(e);
        });
    });
  }

  public createProfileUsers(client: string, profile: IProfileUsers) {
    if (!profile.id) {
      this.action = "create";
      profile.id = this.afDb.createPushId();
    } else {
      this.action = "update";
    }

    return new Promise((resolve, reject) => {
      return this.afDb.database.ref(`Clients/${client}/Profiles/${profile.id}`)
        .update(profile)
        .then(() => {
          return this.historyData.set(
            `Profile content`,
            "profile_users",
            this.action,
            profile,
          );
        })
        .then(() => {
          resolve(profile);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  public createBusinessForm(clientId: string, business: any) {
    if (!business.id) {
      this.action = "create";
      business.id = this.afDb.createPushId();
    } else {
      this.action = "update";
    }

    return new Promise((resolve, reject) => {
      return this.afDb.database.ref(`Clients/${clientId}/Interactivity/Business/Form/${business.id}`)
        .update(business)
        .then(() => {
          return this.historyData.set(
            `Business content`,
            "business_form",
            this.action,
            business,
          );
        })
        .then(() => {
          resolve(business);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  public clearBusinessAgendaForm(clientId: string) {
    return this.afDb.database.ref(`Clients/${clientId}/Interactivity/Business/Agenda`).remove();
  }

  public createBusinessAgendaForm(clientId: string, business: any) {
    if (!business.id) {
      this.action = "create";
      business.id = this.afDb.createPushId();
    } else {
      this.action = "update";
    }

    return new Promise((resolve, reject) => {
      return this.afDb.database.ref(`Clients/${clientId}/Interactivity/Business/Agenda/${business.id}`)
        .update(business)
        .then(() => {
          return this.historyData.set(
            `Business agenda content`,
            "business_form",
            this.action,
            business,
          );
        })
        .then(() => {
          resolve(business);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  public createFormUsers(client: string, formData: IFormsUsers) {
    if (!formData.id) {
      this.action = "create";
      formData.id = this.afDb.createPushId();
    } else {
      this.action = "update";
    }

    return new Promise((resolve, reject) => {
      this.afDb.database.ref(`Clients/${client}/FormUsers/${formData.id}`)
        .update(formData)
        .then(() => {
          return this.historyData.set(
            `Form Users content`,
            "form_users",
            this.action,
            formData,
          );
        })
        .then(() => {
          resolve(formData);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  public setClientAuthConfig(object: any, clientId: string): any {
    object.update_at = new Date().getTime();

    return new Promise((res, rej) => {
      return firebase
        .database()
        .ref(`/ClientList/${clientId}/AuthConfig`)
        .update(object)
        .then(() => {
          res(clientId);
        })
        .catch((e) => {
          rej(e);
        });
    });
  }

  public setRaffleReward(object: any, clientId: string): Promise<any> {
    return new Promise((res, rej) => {
      return firebase
        .database()
        .ref(`/Clients/${clientId}/RaffleReward/${this.afDb.createPushId()}`)
        .update(object)
        .then(() => {
          res(clientId);
        })
        .catch((e) => {
          rej(e);
        });
    });
  }

  public setRaffleConfig(object: any, clientId: string): any {
    object.update_at = new Date().getTime();
    return new Promise((res, rej) => {
      return firebase
        .database()
        .ref(`/Clients/${clientId}/RaffleConfig`)
        .update(object)
        .then(() => {
          res(clientId);
        })
        .catch((e) => {
          rej(e);
        });
    });
  }

  public setUsersRaffle(clientId: string, users: any): Promise<any> {
    // TODO: Deshabilitado xq firestore no permite eliminar en bloque
    // let db = firebase.firestore();
    // let batch = db.batch();
    // let ref;
    // let i = 1;

    // Object.keys(users).forEach(key => {
    //   if ((i % 500) !== 0) {
    //     ref = db.collection(clientId).doc('raffle_private').collection('users').doc(key);
    //     batch.set(ref, users[key])
    //   } else {
    //     ref = db.collection(clientId).doc('raffle_private').collection('users').doc(key);
    //     batch.set(ref, users[key])
    //     batch.commit();
    //     batch = db.batch();
    //   }
    //   i++;
    // });
    // batch.commit();
    // return new Promise(res => res(i - 1));

    return firebase
      .database()
      .ref(`/Clients/${clientId}/RafflePrivate`)
      .set(users);
  }

  public setClientSponsor(clientId: string, object: any, id: string = ""): any {
    if (!id) {
      id = this.afDb.createPushId();
    }

    object.id = id;

    return new Promise((res, rej) => {
      return firebase
        .database()
        .ref(`/ClientList/${clientId}/Sponsor/${id}`)
        .update(object)
        .then(() => {
          res(clientId);
        })
        .catch((e) => {
          rej(e);
        });
    });
  }

  public setUserAdminClient(userId: string, clientId: string): Promise<any> {
    return new Promise((res, rej) => {
      return firebase
        .database()
        .ref(`/AdministratorList/${userId}`)
        .update({ managerClient: clientId })
        .then(() => {
          res(clientId);
        })
        .catch((e) => {
          rej(e);
        });
    });
  }

  public setClientStaff(clientId: string, object: any, staffId: string = ""): any {
    if (!staffId) {
      staffId = this.afDb.createPushId();
    }

    object.id = staffId;

    return new Promise((res, rej) => {
      return firebase
        .database()
        .ref(`/ClientList/${clientId}/Staff/${staffId}`)
        .update(object)
        .then(() => {
          res(clientId);
        })
        .catch((e) => {
          rej(e);
        });
    });
  }

}
// end file



// WEBPACK FOOTER //
// ./src/providers/client.ts